import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { paymentsAPI } from '../services/api';
import { CheckCircle, BookOpen, ArrowRight } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  const txnid = searchParams.get('txnid');

  useEffect(() => {
    if (txnid) {
      fetchTransactionStatus();
    } else {
      setLoading(false);
    }
  }, [txnid]);

  const fetchTransactionStatus = async () => {
    try {
      const response = await paymentsAPI.getStatus(txnid);
      setTransaction(response.data.data);
    } catch (error) {
      console.error('Error fetching transaction status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 text-center text-earth-500">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600 mx-auto"></div>
        <p className="mt-4">Verifying payment...</p>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-16 px-4 max-w-2xl mx-auto bg-background min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="bg-card border border-earth-200 rounded-2xl p-8 text-center"
      >
        <div className="mb-6">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-serif font-bold mb-2 text-earth-900">
            Payment Successful!
          </h1>
          <p className="text-earth-600 text-lg">
            Thank you for your purchase. You now have access to your course.
          </p>
        </div>

        {transaction && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-green-800 mb-4">
              Transaction Details
            </h2>
            <div className="space-y-2 text-left">
              <div className="flex justify-between">
                <span className="text-earth-600">Course:</span>
                <span className="font-medium text-earth-900">{transaction.course_title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-earth-600">Amount:</span>
                <span className="font-medium text-earth-900">₹{transaction.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-earth-600">Transaction ID:</span>
                <span className="font-medium text-earth-900 text-sm">{transaction.txnid}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-earth-600">Date:</span>
                <span className="font-medium text-earth-900">
                  {new Date(transaction.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center w-full py-3 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700 transition-colors font-medium"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Go to My Courses
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
          
          <Link
            to="/"
            className="inline-block w-full py-3 border border-earth-300 text-earth-700 rounded-lg hover:bg-earth-50 transition-colors font-medium"
          >
            Browse More Courses
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-earth-200">
          <p className="text-earth-500 text-sm">
            You will receive a confirmation email shortly. If you have any questions, 
            please contact our support team.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess; 